import {
  Box, Card, LinearProgress, Typography, Chip,
} from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LockOutlined, Check } from '@mui/icons-material';
import { useAlbumIcon, AlbumCategory } from './useAlbumIcon';

interface AlbumProgressCardProps {
  albumId?: string;
  petId?: string;
  title: string;
  description: string;
  completed: number;
  total: number;
  category: AlbumCategory;
  isLocked?: boolean;
}

function AlbumProgressCard({
  albumId,
  petId,
  title,
  description,
  completed,
  total,
  category,
  isLocked = false,
}: AlbumProgressCardProps) {
  const navigate = useNavigate();
  const progress = (completed / total) * 100;
  const isCompleted = completed === total;
  const { icon } = useAlbumIcon(category);

  const handleClick = () => {
    if (!isLocked && albumId && petId) {
      navigate(`/hub/albums/${petId}/${albumId}`);
    }
  };

  return (
    <Card
      onClick={handleClick}
      sx={{
        borderRadius: '12px',
        padding: {
          xs: 3,
          md: 5,
        },
        boxShadow: isLocked ? 'none' : '0px 2px 8px rgba(0, 0, 0, 0.08)',
        backgroundColor: isLocked ? 'rgba(255, 255, 255, 0.55)' : '#fff',
        cursor: isLocked ? 'default' : 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: isLocked ? 'none' : 'translateY(-2px)',
          boxShadow: isLocked ? 'none' : '0px 4px 12px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            backgroundColor: isLocked ? 'grey.100' : 'primary.50',
            color: isLocked ? 'grey.400' : 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h3"
            sx={{
              fontSize: '18px',
              fontWeight: 700,
              color: 'primary.text',
              mb: 1.5,
              letterSpacing: '0.15px',
              lineHeight: '150%',
              opacity: isLocked ? 0.5 : 1,
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mb: 1.5,
              lineHeight: 1.4,
            }}
          >
            {description}
          </Typography>

          {(() => {
            if (isLocked) {
              return (
                <Chip
                  icon={<LockOutlined sx={{ fontSize: '20px' }} />}
                  label="Upgrade to unlock"
                  sx={{
                    backgroundColor: 'grey.300',
                    color: 'primary.text',
                    height: 24,
                    fontSize: '14px',
                    '& .MuiChip-icon': {
                      color: 'primary.text',
                      marginLeft: '10px',
                    },
                  }}
                />
              );
            }

            if (isCompleted) {
              return (
                <Chip
                  icon={<Check sx={{ fontSize: '16px !important' }} />}
                  label={`All ${total} prompts completed`}
                  size="small"
                  sx={{
                    height: 24,
                    backgroundColor: '#ffe0b2',
                    color: 'rgba(0, 0, 0, 0.87)',
                    fontSize: '14px',
                    fontWeight: 400,
                    alignSelf: 'flex-start',
                    '& .MuiChip-icon': {
                      color: 'rgba(0, 0, 0, 0.87)',
                      marginLeft: '6px',
                    },
                    '& .MuiChip-label': {
                      paddingLeft: '6px',
                      paddingRight: '12px',
                    },
                  }}
                />
              );
            }

            return (
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'secondary.text',
                    mb: 0.5,
                    fontSize: '14px',
                  }}
                >
                  {`${completed} / ${total} done`}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      backgroundColor: 'primary.main',
                    },
                  }}
                />
              </Box>
            );
          })()}
        </Box>
      </Box>
    </Card>
  );
}

export default AlbumProgressCard;
